import { Avatar, Box, Grid, Grow, Paper, Typography } from "@mui/material";
import ManRoundedIcon from "@mui/icons-material/ManRounded";
import WomanRoundedIcon from "@mui/icons-material/WomanRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "lib/api/api";

const cardThemes = [
  { background: "#FFF9E8", border: "#F2D98A", avatar: "#FFE7A3", avatarText: "#7A5900" },
  { background: "#F1F8F7", border: "#BFDCD7", avatar: "#D8EFEB", avatarText: "#246C62" },
  { background: "#F7F3FB", border: "#D8C8E8", avatar: "#E9DDF4", avatarText: "#6D468D" },
  { background: "#FFF3F5", border: "#EBC8CF", avatar: "#F8DCE2", avatarText: "#93495A" },
  { background: "#F2F6FC", border: "#C8D7EB", avatar: "#DCE8F7", avatarText: "#35658F" },
  { background: "#F8F7ED", border: "#DEDBB9", avatar: "#ECE9C9", avatarText: "#716C30" },
];

const TeacherFeedbackSection = ({ teacherId, teacherName, placement = "profile" }) => {
  const { t, i18n } = useTranslation(["ui"]);
  const language = i18n.language?.split("-")[0] || "en";
  const isArabic = language === "ar";
  const [feedbacks, setFeedbacks] = useState([]);

  useEffect(() => {
    let active = true;
    if (!teacherId) {
      setFeedbacks([]);
      return () => { active = false; };
    }
    api.get(`/courses/teachers/${teacherId}/feedbacks`)
      .then((response) => { if (active) setFeedbacks(Array.isArray(response.data) ? response.data : []); })
      .catch(() => { if (active) setFeedbacks([]); });
    return () => { active = false; };
  }, [teacherId]);

  if (!feedbacks.length) return null;

  const textFor = (feedback) => {
    if (language === "ar") return feedback.feedbackAr || feedback.feedbackEn || feedback.feedbackDe;
    if (language === "de") return feedback.feedbackDe || feedback.feedbackEn || feedback.feedbackAr;
    return feedback.feedbackEn || feedback.feedbackDe || feedback.feedbackAr;
  };

  return (
    <Box
      component="section"
      dir={isArabic ? "rtl" : "ltr"}
      sx={{
        mt: placement === "course" ? { xs: 4, md: 5 } : { xs: 3, md: 4 },
        mb: { xs: 7, md: 10 },
        mx: "auto",
        width: "100%",
        maxWidth: 1180,
        textAlign: "center",
      }}
    >
      <Typography
        fontWeight={900}
        sx={{ mb: { xs: 3, md: 4 }, fontSize: { xs: "1.5rem", md: "2rem" }, textAlign: "center" }}
      >
        {placement === "course"
          ? t("teacherFeedback.courseTitle", { teacherName })
          : t("teacherFeedback.profileTitle", { teacherName })}
      </Typography>
      <Grid container spacing={3} justifyContent="center" sx={{ direction: isArabic ? "rtl" : "ltr" }}>
        {feedbacks.map((feedback, index) => {
          const female = feedback.studentGender === "FEMALE";
          const theme = cardThemes[index % cardThemes.length];
          return (
            <Grow in timeout={350 + index * 120} key={feedback.id}>
              <Grid item xs={12} sm={6} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%", minHeight: 190, p: { xs: 2.5, md: 3 }, borderRadius: 4,
                    border: `1px solid ${theme.border}`,
                    bgcolor: theme.background,
                    textAlign: isArabic ? "right" : "left",
                    boxShadow: "0 5px 16px rgba(24,39,39,.07)",
                    transition: "transform .25s ease, box-shadow .25s ease",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 10px 24px rgba(24,39,39,.11)" },
                    "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                    <Avatar
                      aria-label={female ? t("teacherFeedback.femaleAvatar") : t("teacherFeedback.maleAvatar")}
                      sx={{ width: 58, height: 58, bgcolor: theme.avatar, color: theme.avatarText, border: `1px solid ${theme.border}` }}
                    >
                      {female ? <WomanRoundedIcon fontSize="large" /> : <ManRoundedIcon fontSize="large" />}
                    </Avatar>
                    <Typography fontWeight={900} sx={{ fontSize: "1.08rem" }}>{feedback.studentName}</Typography>
                  </Box>
                  <Box
                    aria-label={t("teacherFeedback.fiveStars")}
                    sx={{ display: "flex", justifyContent: "flex-start", gap: 0.25, mb: 1.5, color: "#F2B600" }}
                  >
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarRoundedIcon key={star} sx={{ fontSize: 23 }} />
                    ))}
                  </Box>
                  <Typography sx={{ color: "#465151", lineHeight: 1.9, whiteSpace: "pre-line" }}>
                    “{textFor(feedback)}”
                  </Typography>
                </Paper>
              </Grid>
            </Grow>
          );
        })}
      </Grid>
    </Box>
  );
};

export default TeacherFeedbackSection;
